/* In-page anchor navigation. Added by Oleg @ IAKOE.
   ONE module, two web components, zero globals:

     <ts-scroll-nav>  links marked data-ts-scroll="<id>" smooth-scroll to that element
                      (FAQ sidebar, catalog Category nav, anchor buttons)
     <ts-toc>         builds its link list from the article body's headings first, then
                      behaves the same (article Table of contents)

   Both share the private navHighlight() below, which marks the link of the section
   currently being read with `is-active`. Per our architecture the differences are
   expressed as element attributes (data-levels, data-for), not as separate scripts.

   Why one file: the highlighter used to be its own module. A relative import carries no
   `?v=` hash, so Shopify's versioning never reached it and browsers kept the first copy
   they ever cached ("lockTo is not a function" on every nav click). Publishing it on
   window fixed that but forced every consumer to load two script tags in the right
   order, and one consumer shipped half-wired. Same file = no pairing, no ordering, no
   global, one request. (NOT named ts-nav.js: that is the header mega-menu.)

   WHY NOT IntersectionObserver: it delivers asynchronously and only for targets whose
   intersection CHANGED. Batches produced during a locked smooth scroll arrive after the
   lock lifts, carrying stale rectangles, and the highlight lands on the section we
   scrolled away from. Reading live geometry on scroll is deterministic. */

/* ---------- shared highlighter (module-private) ---------- */

/* @param {Array<{target: Element, link: Element}>} pairs
   @param {Object} [options]
   @param {string} [options.activeClass='is-active']
   @param {number} [options.line=0.25] - fallback reading line when a target has no
                   scroll-margin-top, as a share of viewport height.
   @returns {{disconnect, lockTo, update}|null} */
function navHighlight(pairs, options = {}) {
  const items = (pairs || []).filter((p) => p && p.target && p.link);
  if (!items.length) return null;

  const activeClass = options.activeClass || 'is-active';
  const lineRatio = typeof options.line === 'number' ? options.line : 0.25;

  let locked = false;
  let unlockTimer = null;
  let frame = null;
  let arriveFrame = null;

  const setActive = (link) => {
    items.forEach((i) => i.link.classList.toggle(activeClass, i.link === link));
  };

  /* The last section that has reached its landing position is the one being read.
     "Landing position" = the element's own scroll-margin-top, i.e. exactly where a click
     puts it (it clears the sticky header). That keeps click and scroll in agreement; a
     viewport-relative line broke on short windows, where 25% of the height sat ABOVE the
     landing position, so the clicked section never qualified and the previous one stayed
     lit. The ratio remains as a fallback for targets without scroll-margin. */
  const thresholdFor = (target) => {
    const sm = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    return sm > 0 ? sm + 4 : (window.innerHeight || 0) * lineRatio;
  };

  const current = () => {
    let found = items[0];
    for (const item of items) {
      if (item.target.getBoundingClientRect().top <= thresholdFor(item.target)) found = item;
    }
    return found;
  };

  const update = () => {
    if (locked) return;
    const item = current();
    if (item) setActive(item.link);
  };

  const onScroll = () => {
    if (frame) return; // one read per frame, rects are not free
    frame = requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update(); // paint the starting state

  /* Hold the highlighter until we have actually ARRIVED at the clicked target, not
     merely until the page stopped moving: "stopped" fired on every pause the animation
     took, letting the highlight resume mid-flight and run down the list.

     Three ways out, so it can never stay locked: arrival, the visitor taking over the
     scroll, and a safety timeout for targets that can never reach the line (the last
     section on a page that cannot scroll any further). */
  const waitForArrival = (target) => {
    clearTimeout(unlockTimer);
    if (arriveFrame) cancelAnimationFrame(arriveFrame);

    /* Trackpads keep sending inertial `wheel` events after the gesture that brought the
       visitor to the nav. Those arrive right after the click and used to count as "the
       visitor took over", unlocking mid-flight. Ignore gestures for a moment. */
    const GRACE = 350;
    const startedAt = Date.now();
    let settleTimer = null;

    function release() {
      if (arriveFrame) cancelAnimationFrame(arriveFrame);
      arriveFrame = null;
      clearTimeout(unlockTimer);
      clearTimeout(settleTimer);
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchstart', onUserScroll);
      locked = false;
      update(); // always recompute from the final geometry, never leave a stale class
    }

    function onUserScroll() {
      if (Date.now() - startedAt < GRACE) return; // inertia from before the click
      release();
    }

    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchstart', onUserScroll, { passive: true });

    const atEnd = () =>
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;

    /* Arrived = at the landing position AND no longer moving. Position alone is not
       enough: an accordion opening mid-flight reflows the page and the target can pass
       through the line while still travelling. */
    let lastTop = null;
    const tick = () => {
      const landing = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const top = target.getBoundingClientRect().top;
      const parked = Math.abs(top - landing) <= 2 || atEnd();
      if (parked && lastTop !== null && Math.abs(top - lastTop) < 0.5) {
        // Hold one extra beat so a late reflow cannot leave us pointing at the wrong one.
        clearTimeout(settleTimer);
        settleTimer = setTimeout(release, 60);
        return;
      }
      lastTop = top;
      arriveFrame = requestAnimationFrame(tick);
    };
    arriveFrame = requestAnimationFrame(tick);

    unlockTimer = setTimeout(release, 3000);
  };

  return {
    disconnect() {
      clearTimeout(unlockTimer);
      if (frame) cancelAnimationFrame(frame);
      if (arriveFrame) cancelAnimationFrame(arriveFrame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    },
    /* Call on click, right after starting the smooth scroll. The target is looked up
       from the link, so callers do not have to pass it. */
    lockTo(link) {
      const item = items.find((i) => i.link === link);
      if (!item) return;
      setActive(link);
      locked = true;
      waitForArrival(item.target);
    },
    update,
  };
}

/* ---------- <ts-scroll-nav>: highlight + smooth scroll for authored links ----------
   Progressive enhancement: links are real <a href="#id"> and work without JS. The FAQ
   sidebar is filled at runtime by <ts-faq>, so its links do not exist yet on connect:
   we watch our own subtree and (re)build the highlighter once they appear. */

if (!customElements.get('ts-scroll-nav')) {
  customElements.define(
    'ts-scroll-nav',
    class TsScrollNav extends HTMLElement {
      connectedCallback() {
        this._onClick = this.onClick.bind(this);
        this.addEventListener('click', this._onClick);

        this.buildHighlight();
        this._mo = new MutationObserver(() => this.buildHighlight());
        this._mo.observe(this, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        if (this._mo) this._mo.disconnect();
        if (this._highlight) this._highlight.disconnect();
      }

      /* Pair each [data-ts-scroll] link with the element it points at. Guarded by a
         signature so the MutationObserver does not rebuild on every unrelated change. */
      buildHighlight() {
        const links = Array.from(this.querySelectorAll('[data-ts-scroll]'));
        const signature = links.map((a) => a.getAttribute('data-ts-scroll')).join('|');
        if (signature === this._signature) return;
        this._signature = signature;

        if (this._highlight) this._highlight.disconnect();
        const pairs = links
          .map((link) => {
            const id = (link.getAttribute('data-ts-scroll') || '').trim();
            const target = id ? document.getElementById(id) : null;
            return target ? { target, link } : null;
          })
          .filter(Boolean);
        this._highlight = navHighlight(pairs);
      }

      onClick(event) {
        const trigger = event.target.closest('[data-ts-scroll]');
        if (!trigger || !this.contains(trigger)) return;

        const id = (trigger.getAttribute('data-ts-scroll') || '').trim();
        if (!id) return; // no anchor configured -> let the browser do nothing

        const target = document.getElementById(id);
        if (!target) return; // anchor target not on this page -> no-op, no error

        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Light up the destination immediately instead of letting the highlight run
        // through every section the smooth scroll passes on the way.
        if (this._highlight) this._highlight.lockTo(trigger);
      }
    }
  );
}

/* ---------- <ts-toc>: builds its links from the article body, then highlights ----------
   Attributes: data-levels (heading selector, default h2), data-for (body selector,
   default the closest .ts-article's .ts-article__body). Hides its own .ts-toc-block when
   the body has no matching headings, so the sidebar never shows an empty card. */

if (!customElements.get('ts-toc')) {
  customElements.define(
    'ts-toc',
    class TsToc extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-toc-list]');
        if (!this.list) return;

        const selector = this.dataset.levels || 'h2';
        const body = this.bodyEl();
        if (!body) return this.hideBlock();

        const headings = Array.from(body.querySelectorAll(selector)).filter(
          (h) => (h.textContent || '').trim().length
        );
        if (!headings.length) return this.hideBlock();

        const used = new Set();
        this.links = headings.map((h) => {
          if (!h.id || used.has(h.id)) h.id = this.uniqueId(h.textContent, used);
          used.add(h.id);

          const a = document.createElement('a');
          a.className = 'ts-toc__link';
          a.href = '#' + h.id;
          a.textContent = (h.textContent || '').trim();
          a.addEventListener('click', (e) => {
            e.preventDefault();
            h.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.replaceState(null, '', '#' + h.id);
            // Same immediate-highlight-and-hold as ts-scroll-nav.
            if (this._highlight) this._highlight.lockTo(a);
          });

          const li = document.createElement('li');
          li.className = 'ts-toc__item';
          li.appendChild(a);
          this.list.appendChild(li);
          return { heading: h, link: a };
        });

        this._highlight = navHighlight(
          this.links.map((l) => ({ target: l.heading, link: l.link }))
        );
      }

      disconnectedCallback() {
        if (this._highlight) this._highlight.disconnect();
      }

      bodyEl() {
        if (this.dataset.for) return document.querySelector(this.dataset.for);
        const root = this.closest('.ts-article');
        return root ? root.querySelector('.ts-article__body') : null;
      }

      uniqueId(text, used) {
        let base =
          (text || '')
            .toLowerCase()
            .trim()
            .replace(/[^\p{L}\p{N}]+/gu, '-')
            .replace(/^-+|-+$/g, '') || 'section';
        let id = base;
        let i = 2;
        while (used.has(id) || document.getElementById(id)) id = base + '-' + i++;
        return id;
      }

      hideBlock() {
        const block = this.closest('.ts-toc-block') || this;
        block.hidden = true;
      }
    }
  );
}
